import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';

export class CosmicBackground {
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private renderer: THREE.WebGLRenderer;
  private composer: EffectComposer;
  private particles: THREE.Points | null = null;
  private stars: THREE.Points | null = null;
  private nebulaLayer1: THREE.Points | null = null;
  private nebulaLayer2: THREE.Points | null = null;
  private nebulaLayer3: THREE.Points | null = null;
  private clock: THREE.Clock;

  private particleCount = 15000;
  private starCount = 7000;

  private currentPattern = 0;
  private isTransitioning = false;
  private transitionProgress = 0;
  private transitionSpeed = 0.015;

  private animationId: number | null = null;
  private isDisposed = false;

  private patternPalettes = [
    [new THREE.Color(0xFF6B00), new THREE.Color(0xFFA500), new THREE.Color(0xFFD700)],
    [new THREE.Color(0xFF4500), new THREE.Color(0xFF6B00), new THREE.Color(0xF7931A)],
    [new THREE.Color(0xF7931A), new THREE.Color(0xFFB347), new THREE.Color(0xFFD700)],
    [new THREE.Color(0xCC5500), new THREE.Color(0xFF6B00), new THREE.Color(0xFFA500)],
  ];

  constructor(containerId: string) {
    this.clock = new THREE.Clock();

    // Scene setup
    this.scene = new THREE.Scene();
    this.scene.fog = new THREE.FogExp2(0x0a0500, 0.008);

    // Camera setup
    this.camera = new THREE.PerspectiveCamera(60, window.innerWidth / window.innerHeight, 0.1, 2000);
    this.camera.position.z = 50;

    // Renderer setup
    const container = document.getElementById(containerId);
    if (!container) throw new Error(`Container ${containerId} not found`);

    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    container.appendChild(this.renderer.domElement);

    // Post-processing
    this.composer = new EffectComposer(this.renderer);
    this.composer.addPass(new RenderPass(this.scene, this.camera));

    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(window.innerWidth, window.innerHeight),
      0.35,
      0.4,
      0.8
    );
    this.composer.addPass(bloomPass);
    this.composer.addPass(new OutputPass());

    // Create elements
    this.stars = this.createStarfield();
    this.scene.add(this.stars);

    this.nebulaLayer1 = this.createNebulaStars(200, 15, 8, 700, 0.15);
    this.nebulaLayer2 = this.createNebulaStars(300, 8, 5, 600, 0.3);
    this.nebulaLayer3 = this.createNebulaStars(400, 5, 3, 500, 0.5);
    this.scene.add(this.nebulaLayer1, this.nebulaLayer2, this.nebulaLayer3);

    this.particles = this.createParticleSystem();
    this.scene.add(this.particles);

    // Set initial pattern
    this.setInitialPattern();

    // Event listeners
    window.addEventListener('resize', this.onWindowResize.bind(this));
    window.addEventListener('scroll', this.onScroll.bind(this), { passive: true });

    // Start animation
    this.animate();
  }

  private normalizePoints(points: THREE.Vector3[], size: number): THREE.Vector3[] {
    if (points.length === 0) return points;
    const box = new THREE.Box3().setFromPoints(points);
    if (box.isEmpty() || !isFinite(box.max.x) || !isFinite(box.min.x)) {
      points.forEach(p => p.set(0, 0, 0));
      return points;
    }
    const center = box.getCenter(new THREE.Vector3());
    const boxSize = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(boxSize.x, boxSize.y, boxSize.z);
    const scale = (maxDim > 1e-6) ? size / maxDim : 1.0;
    points.forEach(p => {
      if (isFinite(p.x) && isFinite(p.y) && isFinite(p.z)) {
        p.sub(center).multiplyScalar(scale);
      } else {
        p.set(0, 0, 0);
      }
    });
    return points;
  }

  private createTorusKnotPoints(count: number): THREE.Vector3[] {
    const points: THREE.Vector3[] = [];
    const radius = 18, tube = 6, p = 5, q = 4, twists = 2;
    for (let i = 0; i < count; i++) {
      const t = (i / count) * Math.PI * 2 * q;
      const angle = p / q * t;
      const R = radius + tube * Math.cos(t);
      const baseX = R * Math.cos(angle);
      const baseY = R * Math.sin(angle);
      const baseZ = tube * Math.sin(t);
      const twistAngle = twists * t;
      const twistRadius = 1.5;
      const normal = new THREE.Vector3(Math.cos(angle), Math.sin(angle), 0);
      const tangent = R > 1e-6 ? new THREE.Vector3(-R * Math.sin(angle), R * Math.cos(angle), tube * Math.cos(t)).normalize() : new THREE.Vector3(0, 0, 1);
      const binormal = new THREE.Vector3().crossVectors(tangent, normal).normalize();
      const x = baseX + twistRadius * (binormal.x * Math.cos(twistAngle) + normal.x * Math.sin(twistAngle));
      const y = baseY + twistRadius * (binormal.y * Math.cos(twistAngle) + normal.y * Math.sin(twistAngle));
      const z = baseZ + twistRadius * (binormal.z * Math.cos(twistAngle) + normal.z * Math.sin(twistAngle));
      points.push(new THREE.Vector3(x, y, z));
    }
    return this.normalizePoints(points, 65);
  }

  private createEllipticalOrbitPoints(count: number): THREE.Vector3[] {
    const points: THREE.Vector3[] = [];
    const numOrbits = 8;
    const particlesPerOrbit = Math.floor(count / numOrbits);
    const baseRadius = 6, orbitSpacing = 4;
    for (let orbit = 0; orbit < numOrbits; orbit++) {
      const eccentricity = 0.5 + Math.random() * 0.45;
      const a = baseRadius + orbit * orbitSpacing * (1 + (orbit / numOrbits) * 0.5);
      const b = a * Math.sqrt(1 - Math.min(eccentricity, 0.999) ** 2);
      const tiltX = (Math.random() - 0.5) * Math.PI / 2.5;
      const tiltY = (Math.random() - 0.5) * Math.PI / 2.5;
      const rotation = new THREE.Quaternion().setFromEuler(new THREE.Euler(tiltX, tiltY, Math.random() * Math.PI));
      for (let i = 0; i < particlesPerOrbit; i++) {
        const theta = (i / particlesPerOrbit) * Math.PI * 2;
        const radiusFactor = 1.0 + (Math.random() - 0.5) * 0.1;
        const angleOffset = (Math.random() - 0.5) * 0.05;
        const x = a * Math.cos(theta + angleOffset) * radiusFactor;
        const y = b * Math.sin(theta + angleOffset) * radiusFactor;
        const zWave = 1.5 * Math.sin(theta * 3 + orbit * 0.5);
        const point = new THREE.Vector3(x, y, zWave).applyQuaternion(rotation);
        points.push(point);
      }
    }
    for (let i = points.length; i < count; i++) {
      points.push(points[i % points.length].clone().multiplyScalar(1 + (Math.random() - 0.5) * 0.05));
    }
    return this.normalizePoints(points, 70);
  }

  private createWaveOrbitPoints(count: number): THREE.Vector3[] {
    const points: THREE.Vector3[] = [];
    const baseRadius = 20, rAmp1 = 6, rFreq1 = 3, rAmp2 = 2.5, rFreq2 = 7;
    const zAmp1 = 5, zFreq1 = 4, zAmp2 = 2, zFreq2 = 11;
    for (let i = 0; i < count; i++) {
      const t = (i / count) * Math.PI * 2;
      const currentRadius = baseRadius + rAmp1 * Math.sin(rFreq1 * t) + rAmp2 * Math.cos(rFreq2 * t + Math.PI / 3);
      const x = currentRadius * Math.cos(t);
      const y = currentRadius * Math.sin(t);
      const z = zAmp1 * Math.sin(zFreq1 * t + Math.PI / 5) + zAmp2 * Math.cos(zFreq2 * t);
      points.push(new THREE.Vector3(x, y, z));
    }
    return this.normalizePoints(points, 60);
  }

  private createCosmicWebPoints(count: number): THREE.Vector3[] {
    const points: THREE.Vector3[] = [];
    const numStrands = 15;
    const particlesPerStrand = Math.floor(count / numStrands);
    const scale = 80;
    for (let i = 0; i < numStrands; i++) {
      const start = new THREE.Vector3(
        (Math.random() - 0.5) * scale,
        (Math.random() - 0.5) * scale,
        (Math.random() - 0.5) * scale
      );
      const end = new THREE.Vector3(
        (Math.random() - 0.5) * scale,
        (Math.random() - 0.5) * scale,
        (Math.random() - 0.5) * scale
      );
      const control1 = start.clone().lerp(end, 0.33).add(new THREE.Vector3().randomDirection().multiplyScalar(scale / 4));
      const control2 = start.clone().lerp(end, 0.66).add(new THREE.Vector3().randomDirection().multiplyScalar(scale / 4));
      const curve = new THREE.CubicBezierCurve3(start, control1, control2, end);
      const strandPoints = curve.getPoints(particlesPerStrand - 1);
      strandPoints.forEach(p => {
        points.push(p);
      });
    }
    for (let i = points.length; i < count; i++) {
      points.push(points[i % points.length].clone().multiplyScalar(1 + (Math.random() - 0.5) * 0.05));
    }
    return this.normalizePoints(points, 80);
  }

  private patterns = [
    this.createTorusKnotPoints.bind(this),
    this.createEllipticalOrbitPoints.bind(this),
    this.createWaveOrbitPoints.bind(this),
    this.createCosmicWebPoints.bind(this)
  ];

  private createStarfield(): THREE.Points {
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(this.starCount * 3);
    const colors = new Float32Array(this.starCount * 3);
    const randoms = new Float32Array(this.starCount);
    const starRadius = 800;

    for (let i = 0; i < this.starCount; i++) {
      const theta = Math.random() * 2 * Math.PI;
      const phi = Math.acos(2 * Math.random() - 1);
      positions[i * 3] = starRadius * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = starRadius * Math.sin(phi) * Math.sin(theta);
      positions[i * 3 + 2] = starRadius * Math.cos(phi);
      const hue = 0.05 + Math.random() * 0.08;
      const color = new THREE.Color().setHSL(hue, 0.8, 0.6);
      colors[i * 3] = color.r;
      colors[i * 3 + 1] = color.g;
      colors[i * 3 + 2] = color.b;
      randoms[i] = Math.random() * Math.PI * 2;
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geometry.setAttribute('random', new THREE.BufferAttribute(randoms, 1));

    const material = new THREE.ShaderMaterial({
      uniforms: { time: { value: 0 } },
      vertexShader: `
        uniform float time;
        attribute float random;
        varying vec3 vColor;
        void main() {
          vColor = color;
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          float size = 1.5 + 0.5 * sin(time * 0.5 + random);
          gl_PointSize = size * (300.0 / -mvPosition.z);
          gl_Position = projectionMatrix * mvPosition;
        }`,
      fragmentShader: `
        varying vec3 vColor;
        void main() {
          if (length(gl_PointCoord - vec2(0.5, 0.5)) > 0.48) discard;
          gl_FragColor = vec4(vColor, 1.0);
        }`,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      vertexColors: true
    });

    return new THREE.Points(geometry, material);
  }

  private createNebulaStars(numStars: number, baseSize: number, sizeVariation: number, radius: number, opacity: number): THREE.Points {
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(numStars * 3);
    const colors = new Float32Array(numStars * 3);
    const sizes = new Float32Array(numStars);

    for (let i = 0; i < numStars; i++) {
      const theta = Math.random() * 2 * Math.PI;
      const phi = Math.acos(2 * Math.random() - 1);
      positions[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
      positions[i * 3 + 2] = radius * Math.cos(phi);
      const hue = 0.05 + Math.random() * 0.1;
      const color = new THREE.Color().setHSL(hue, 0.3, 0.6);
      colors[i * 3] = color.r;
      colors[i * 3 + 1] = color.g;
      colors[i * 3 + 2] = color.b;
      sizes[i] = baseSize + Math.random() * sizeVariation;
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

    const material = new THREE.ShaderMaterial({
      uniforms: { uOpacity: { value: opacity } },
      vertexShader: `
        attribute float size;
        varying vec3 vColor;
        void main() {
          vColor = color;
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = size * (300.0 / -mvPosition.z);
          gl_Position = projectionMatrix * mvPosition;
        }`,
      fragmentShader: `
        uniform float uOpacity;
        varying vec3 vColor;
        void main() {
          float dist = length(gl_PointCoord - vec2(0.5));
          if (dist > 0.5) discard;
          float alpha = 1.0 - smoothstep(0.4, 0.5, dist);
          gl_FragColor = vec4(vColor, alpha * uOpacity);
        }`,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      vertexColors: true
    });

    return new THREE.Points(geometry, material);
  }

  private createParticleSystem(): THREE.Points {
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(this.particleCount * 3);
    const colors = new Float32Array(this.particleCount * 3);
    const sizes = new Float32Array(this.particleCount);
    const randoms = new Float32Array(this.particleCount);

    for (let i = 0; i < this.particleCount; i++) {
      sizes[i] = 0.5 + Math.random() * 3.5;
      randoms[i] = Math.random() * Math.PI * 2;
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
    geometry.setAttribute('random', new THREE.BufferAttribute(randoms, 1));

    const material = new THREE.ShaderMaterial({
      uniforms: { time: { value: 0 } },
      vertexShader: `
        uniform float time;
        attribute float size;
        attribute float random;
        varying vec3 vColor;
        void main() {
          vColor = color;
          vec3 pos = position;
          float angle = time * 0.4 + random;
          float displacement = 0.5 + sin(random * 2.0) * 0.2;
          pos.x += sin(angle + pos.y * 0.1) * displacement;
          pos.y += cos(angle + pos.z * 0.1) * displacement;
          pos.z += sin(angle + pos.x * 0.1) * displacement;
          vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
          float pulse = 0.95 + 0.05 * sin(time * 1.5 + random);
          gl_Position = projectionMatrix * mvPosition;
          gl_PointSize = size * pulse * (300.0 / -mvPosition.z);
        }`,
      fragmentShader: `
        varying vec3 vColor;
        void main() {
          float dist = length(gl_PointCoord - vec2(0.5));
          if (dist > 0.5) discard;
          float alpha = 1.0 - smoothstep(0.3, 0.5, dist);
          gl_FragColor = vec4(vColor, alpha);
        }`,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      vertexColors: true
    });

    return new THREE.Points(geometry, material);
  }

  private setInitialPattern(): void {
    if (!this.particles) return;

    const points = this.patterns[this.currentPattern](this.particleCount);
    const positions = this.particles.geometry.attributes.position.array as Float32Array;

    for (let i = 0; i < this.particleCount; i++) {
      const idx = i * 3;
      if (points[i]) {
        positions[idx] = points[i].x;
        positions[idx + 1] = points[i].y;
        positions[idx + 2] = points[i].z;
      } else {
        positions[idx] = 0;
        positions[idx + 1] = 0;
        positions[idx + 2] = 0;
      }
    }
    this.particles.geometry.attributes.position.needsUpdate = true;

    const palette = this.patternPalettes[this.currentPattern];
    const colors = this.particles.geometry.attributes.color.array as Float32Array;

    for (let i = 0; i < this.particleCount; i++) {
      const color = palette[i % palette.length];
      colors[i * 3] = color.r;
      colors[i * 3 + 1] = color.g;
      colors[i * 3 + 2] = color.b;
    }
    this.particles.geometry.attributes.color.needsUpdate = true;
  }

  private startTransition(targetPattern: number): void {
    if (!this.particles || this.currentPattern === targetPattern) return;

    this.currentPattern = targetPattern;
    this.isTransitioning = true;
    this.transitionProgress = 0;

    const nextPoints = this.patterns[this.currentPattern](this.particleCount);

    this.particles.userData.fromPositions = new Float32Array(this.particles.geometry.attributes.position.array);
    this.particles.userData.toPositions = new Float32Array(this.particleCount * 3);

    for (let i = 0; i < this.particleCount; i++) {
      const idx = i * 3;
      if (nextPoints[i]) {
        this.particles.userData.toPositions[idx] = nextPoints[i].x;
        this.particles.userData.toPositions[idx + 1] = nextPoints[i].y;
        this.particles.userData.toPositions[idx + 2] = nextPoints[i].z;
      } else {
        this.particles.userData.toPositions[idx] = this.particles.userData.fromPositions[idx];
        this.particles.userData.toPositions[idx + 1] = this.particles.userData.fromPositions[idx + 1];
        this.particles.userData.toPositions[idx + 2] = this.particles.userData.fromPositions[idx + 2];
      }
    }

    const nextPalette = this.patternPalettes[this.currentPattern];
    this.particles.userData.fromColors = new Float32Array(this.particles.geometry.attributes.color.array);
    this.particles.userData.toColors = new Float32Array(this.particleCount * 3);

    for (let i = 0; i < this.particleCount; i++) {
      const color = nextPalette[i % nextPalette.length];
      this.particles.userData.toColors[i * 3] = color.r;
      this.particles.userData.toColors[i * 3 + 1] = color.g;
      this.particles.userData.toColors[i * 3 + 2] = color.b;
    }
  }

  private updateTransition(): void {
    if (!this.isTransitioning || !this.particles) return;

    this.transitionProgress += this.transitionSpeed;
    let easedProgress = 0.5 - Math.cos(this.transitionProgress * Math.PI) * 0.5;

    if (this.transitionProgress >= 1) {
      easedProgress = 1;
      this.isTransitioning = false;
    }

    if (!this.particles.userData.fromPositions) return;

    const positions = this.particles.geometry.attributes.position.array as Float32Array;
    const fromPos = this.particles.userData.fromPositions;
    const toPos = this.particles.userData.toPositions;

    for (let i = 0; i < positions.length; i++) {
      positions[i] = fromPos[i] + (toPos[i] - fromPos[i]) * easedProgress;
    }
    this.particles.geometry.attributes.position.needsUpdate = true;

    const colors = this.particles.geometry.attributes.color.array as Float32Array;
    const fromColors = this.particles.userData.fromColors;
    const toColors = this.particles.userData.toColors;

    for (let i = 0; i < colors.length; i++) {
      colors[i] = fromColors[i] + (toColors[i] - fromColors[i]) * easedProgress;
    }
    this.particles.geometry.attributes.color.needsUpdate = true;

    if (!this.isTransitioning) {
      delete this.particles.userData.fromPositions;
      delete this.particles.userData.toPositions;
      delete this.particles.userData.fromColors;
      delete this.particles.userData.toColors;
    }
  }

  private onScroll(): void {
    // Calculate scroll progress (0 to 1)
    const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
    const scrollProgress = scrollHeight > 0 ? window.scrollY / scrollHeight : 0;

    // Determine which pattern to show based on scroll position
    // Divide the page into sections for each pattern
    const numPatterns = this.patterns.length;
    const targetPattern = Math.min(Math.floor(scrollProgress * numPatterns), numPatterns - 1);

    // Only trigger transition if we crossed into a new section
    if (targetPattern !== this.currentPattern && !this.isTransitioning) {
      this.startTransition(targetPattern);
    }
  }

  private onWindowResize(): void {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.composer.setSize(window.innerWidth, window.innerHeight);
  }

  private animate(): void {
    if (this.isDisposed) return;

    this.animationId = requestAnimationFrame(this.animate.bind(this));

    const elapsedTime = this.clock.getElapsedTime();

    this.updateTransition();

    // Camera orbit
    const orbitRadius = 60 - 20 * Math.sin(this.transitionProgress * Math.PI);
    this.camera.position.x = Math.sin(elapsedTime * 0.08) * orbitRadius;
    this.camera.position.z = Math.cos(elapsedTime * 0.08) * orbitRadius;
    this.camera.position.y = Math.sin(elapsedTime * 0.1) * 20;
    this.camera.lookAt(0, 0, 0);

    // Update uniforms
    if (this.particles) {
      (this.particles.material as THREE.ShaderMaterial).uniforms.time.value = elapsedTime;
    }
    if (this.stars) {
      (this.stars.material as THREE.ShaderMaterial).uniforms.time.value = elapsedTime;
    }

    // Rotate nebula layers
    if (this.nebulaLayer1) this.nebulaLayer1.rotation.y += 0.00005;
    if (this.nebulaLayer2) this.nebulaLayer2.rotation.y += 0.0001;
    if (this.nebulaLayer3) this.nebulaLayer3.rotation.y += 0.00015;

    this.composer.render();
  }

  public dispose(): void {
    this.isDisposed = true;

    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }

    window.removeEventListener('resize', this.onWindowResize.bind(this));
    window.removeEventListener('scroll', this.onScroll.bind(this));

    // Dispose geometries and materials
    if (this.particles) {
      this.particles.geometry.dispose();
      (this.particles.material as THREE.Material).dispose();
    }
    if (this.stars) {
      this.stars.geometry.dispose();
      (this.stars.material as THREE.Material).dispose();
    }
    if (this.nebulaLayer1) {
      this.nebulaLayer1.geometry.dispose();
      (this.nebulaLayer1.material as THREE.Material).dispose();
    }
    if (this.nebulaLayer2) {
      this.nebulaLayer2.geometry.dispose();
      (this.nebulaLayer2.material as THREE.Material).dispose();
    }
    if (this.nebulaLayer3) {
      this.nebulaLayer3.geometry.dispose();
      (this.nebulaLayer3.material as THREE.Material).dispose();
    }

    this.renderer.dispose();
    this.composer.dispose();
  }
}
